/**
 * /components/:widgetId/form (contact forms only): which fields the form has.
 *   Address   ask for the visitor's address: postcode look-up, then they choose
 *             their home (required when switched on)
 *   Topic     an optional "What is your enquiry about?" drop-down with the
 *             installer's own choices, required or optional
 * Name, email, phone (optional), message and consent are always there. Saved on
 * the component's branding (branding.form), so the preview and the live form both
 * read it; the public API checks every enquiry against it.
 */
import { useEffect, useMemo, useState } from "react";
import { normaliseBranding, type ContactFormOptions } from "@widget";
import { useApi } from "../../api";
import { Field, Toggle } from "../../components/controls";
import { WidgetPreview } from "../../components/WidgetPreview";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";

export function ContactFormPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const branding = useMemo(() => normaliseBranding(widget.branding), [widget.branding]);
  const [form, setForm, discardDraft] = useTabDraft<ContactFormOptions>("form", branding.form);
  // The choices as typed (one per line), so blank lines don't vanish mid-edit
  const [topicsText, setTopicsText] = useState(form.topics.join("\n"));
  // Re-sync only when the list really differs from the text (e.g. after Discard), not on each keystroke
  useEffect(() => {
    const parsed = topicsText.split("\n").map((t) => t.trim()).filter(Boolean).slice(0, 12);
    if (JSON.stringify(parsed) !== JSON.stringify(form.topics)) setTopicsText(form.topics.join("\n"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.topics]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const dirty = JSON.stringify(form) !== JSON.stringify(branding.form);
  const blocked = form.topicEnabled && form.topics.length === 0;
  const set = (patch: Partial<ContactFormOptions>) => {
    setForm((f) => ({ ...f, ...patch }));
    setMessage(null);
  };

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { branding: { ...branding, form } }));
      discardDraft();
      setMessage({ tone: "success", text: "Saved. Your live form updates within a minute." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  useSaveBar({ dirty, saving, blocked, onSave: save, onDiscard: () => discardDraft() });

  return (
    <div className="editor">
      <aside className="editor-panel">
        {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
        <section className="card">
          <h2>Fields</h2>
          <p className="card-sub">Every form asks for a name, email, phone (optional) and a message, with your consent wording. Add these if you need them.</p>

          <Toggle id="cf-address" checked={form.askAddress} label="Ask for their address" onChange={(v) => set({ askAddress: v })} />
          <p className="ed-hint">They enter their postcode and choose their home from the list (homes with an energy certificate). Required when switched on.</p>

          <div className="cf-gap" />
          <Toggle id="cf-topic" checked={form.topicEnabled} label="Ask what the enquiry is about" onChange={(v) => set({ topicEnabled: v })} />
          {form.topicEnabled && (
            <>
              <Field label="Question" htmlFor="cf-topic-label">
                <input id="cf-topic-label" className="input" maxLength={80} value={form.topicLabel} onChange={(e) => set({ topicLabel: e.target.value })} />
              </Field>
              <Field label="Choices (one per line)" htmlFor="cf-topics" hint="Up to 12. They appear in this order.">
                <textarea
                  id="cf-topics"
                  className="input"
                  rows={Math.max(5, form.topics.length + 2)}
                  value={topicsText}
                  onChange={(e) => {
                    setTopicsText(e.target.value);
                    set({ topics: e.target.value.split("\n").map((t) => t.trim()).filter(Boolean).slice(0, 12) });
                  }}
                />
              </Field>
              {blocked && <div className="flash flash-danger">Add at least one choice, or switch the question off.</div>}
              <Toggle id="cf-topic-req" checked={form.topicRequired} label="They must choose one" onChange={(v) => set({ topicRequired: v })} />
            </>
          )}
        </section>
        <section className="card">
          <h2>Where enquiries go</h2>
          <p className="card-sub">
            Each message is emailed to your notification addresses (Notifications tab), with replies going straight to the visitor. Like callback requests, we don't keep a
            copy. The heading, intro, button and thank-you wording are on the Branding tab.
          </p>
        </section>
      </aside>
      {/* The live preview, with these fields */}
      <div className="editor-preview">
        <WidgetPreview branding={{ ...branding, form }} pricing={widget.pricing} />
      </div>
    </div>
  );
}
